import { ForwardedRef, forwardRef } from 'react'
import cx from 'classnames'
import { useOption } from '@react-aria/listbox'
import { useObjectRef } from 'utils/useObjectRef'
import { NonVirtualizedOptionProps } from '../Types'
import Styles from '../Option.module.scss'
import { Check2 } from 'blaze-ui/icons'
import { Item } from '@react-stately/collections'
import { ItemProps } from '@react-types/shared'

function Option<T extends object>(
  { item, state, className }: NonVirtualizedOptionProps<T>,
  fRef: ForwardedRef<HTMLDivElement>,
) {
  const ref = useObjectRef(fRef)
  const { rendered, key } = item
  const { withCheckmark = true } = item.props
  const { optionProps, isSelected, isDisabled } = useOption({ key, shouldFocusOnHover: false }, state, ref)

  const isFocused = state.selectionManager.focusedKey === key

  if (isFocused) {
    ref.current?.scrollIntoViewIfNeeded?.(false)
  }

  return (
    <div
      {...optionProps}
      ref={ref}
      className={cx(Styles.root, className, {
        [Styles.focused]: isFocused,
        [Styles.disabled]: isDisabled,
        [Styles.selected]: isSelected,
      })}
    >
      <div className={Styles.content}>{rendered}</div>
      {isSelected && withCheckmark && (
        <div className={Styles.checkIcon}>
          <Check2 />
        </div>
      )}
    </div>
  )
}

interface OptionItemProps<T> extends ItemProps<T> {
  withCheckmark?: boolean
}

export const OptionItem: <T>(props: OptionItemProps<T>) => JSX.Element = Item

const _Option = forwardRef(Option)
export { _Option as Option }
